import { useCallback, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { getSessions, SessionPayload, sessionsAtom } from '../services/session.service';

// session descriptions can change during an event (new meeting link, room move)
const REFRESH_MS = 60000;

/**
 * Sessions for a hackathon (id or slug), refreshed periodically and when the tab regains focus.
 */
export const useSessions = (hackathonId: string | undefined) => {
    const all = useAtomValue(sessionsAtom);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<unknown>(null);

    const reload = useCallback(() => {
        if (!hackathonId) {
            return Promise.resolve([] as SessionPayload[]);
        }
        return getSessions(hackathonId).then(sessions => {
            setError(null);
            return sessions;
        }, err => {
            setError(err);
            return [] as SessionPayload[];
        }).finally(() => setLoading(false));
    }, [hackathonId]);

    useEffect(() => {
        if (!hackathonId) {
            return;
        }
        reload();
        const interval = window.setInterval(() => {
            if (document.visibilityState === 'visible') {
                reload();
            }
        }, REFRESH_MS);
        const onFocus = () => reload();
        window.addEventListener('focus', onFocus);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener('focus', onFocus);
        };
    }, [hackathonId, reload]);

    return {
        sessions: (hackathonId && all[hackathonId]) || [],
        loading: !!hackathonId && loading,
        error,
        reload
    };
}
